import { createSiteForm } from "./create-site.js";
import { V } from "../../app/pages.js";
import { inSite, selectedSite, siteById, siteCode } from "./model.js";
import { S } from "../../app/store.js";
import { can, canInvoice, emp } from "../../services/access.js";
import { sitePayroll } from "../../domain/payroll.js";
import { reportRows } from "../reporting/model.js";
import { fmtD, today } from "../../domain/dates.js";
import { sitePicker } from "./components.js";
import { esc, peso } from "../../domain/format.js";
import { panel, ssn } from "../../components/ui.js";
import { ACC } from "../../domain/reference.js";
import { invoicePartySummary } from "../invoices/identity.js";
import { invoiceParties } from "../../domain/invoicing.js";

V.sites = () => {
  const code = selectedSite(),
    site = siteById(code),
    p = S.projects.find((p) => p.siteId === code),
    financial = can("fs.view"),
    salary = can("hr.salary") || can("pay.approve"),
    payroll = can("pay.view"),
    invoices = S.invoices.filter((i) => inSite(i.project)),
    entries = S.je.filter(
      (j) =>
        j.status === "POSTED" &&
        inSite(j.project || S.invoices.find((i) => i.no === j.src)?.project) &&
        !S.runs.some((r) => r.id === j.src),
    );
  const runs = S.runs.map((r) => ({
      r,
      lines: sitePayroll(r).filter((l) => inSite(l.project)),
    })),
    rows = reportRows("0000-01-01", today()).filter(
      (r) => selectedSite() === "ALL" || r.code === selectedSite(),
    ),
    expense = rows.reduce((a, r) => a + r.expenses, 0),
    revenue = rows.reduce((a, r) => a + r.revenue, 0),
    cost = rows.reduce((a, r) => a + r.payroll, 0);
  return `<div class="page-toolbar">${sitePicker()}${createSiteForm()}</div><div class="site-context"><h2>${esc(code === "ALL" ? "ALL · Consolidated" : site ? site.code : "Head office / unassigned")}</h2><p class="muted">${esc(site ? `${site.name} · ${S.projects.filter((p) => p.siteId === code).length} projects` : "Company costs and staff without a site assignment")}</p>
 ${code === "ALL" ? `<p class="muted">Every location and head office, consolidated.</p>` : ""}</div>${panel(
   "Projects at this location",
   "Three-letter project codes; each project belongs to one site.",
   can("gl.create")
     ? `<button data-go="projects">Manage projects</button>`
     : "",
   `<div class="inbox">${
     S.projects
       .filter((p) => inSite(p.code))
       .map(
         (p) =>
           `<div><div><strong>${esc(p.code)}</strong> · ${esc(p.name)}<div class="small muted">${esc(siteCode(p))} · ${esc(p.site)}</div></div>${canInvoice() ? `<button data-act="inv-prep" data-id="${esc(p.code)}">New invoice</button>` : ""}</div>`,
       )
       .join("") || '<p class="muted">No projects.</p>'
   }</div>`,
 )}${financial ? panel("Revenue & expenses", "Cumulative recorded amounts · approved payroll only", "", `<div class="inbox"><div><span>Revenue</span><strong>${peso(revenue)}</strong></div><div><span>Payroll cost (including historical payroll)</span><strong>${peso(cost)}</strong></div><div><span>Other posted expenses</span><strong>${peso(expense)}</strong></div><div><span>Result before tax</span><strong>${peso(revenue - cost - expense)}</strong></div></div><p class="small muted">Figures come from posted entries. Historical sample revenue is attributed to locations by their recognized project revenue. Historical expenses without a site stay under Head office / unassigned. Draft expenses and payroll are excluded.</p>`) : ""}
 ${
   payroll
     ? panel(
         "Site payroll",
         "Allocated from the time records for each cut-off; approval and release apply to the full company run.",
         "",
         runs
           .filter((x) => x.lines.length)
           .map(
             ({ r, lines }) =>
               `<h3>${esc(r.id)} · ${fmtD(r.from)} – ${fmtD(r.to)} ${ssn(r.status)}</h3><div class="tbl"><table><thead><tr><th>Employee</th>${salary ? "<th class='n'>Gross</th><th class='n'>Allowances</th><th class='n'>Net pay</th><th class='n'>Employer share</th>" : ""}</tr></thead><tbody>${lines.map((l) => `<tr><td>${esc(emp(l.emp)?.name || l.emp)}</td>${salary ? ["gross", "allow", "net", "er"].map((k) => `<td class="n">${peso(l[k])}</td>`).join("") : ""}</tr>`).join("")}</tbody>${salary ? `<tfoot><tr><td>Site totals</td>${["gross", "allow", "net", "er"].map((k) => `<td class="n">${peso(lines.reduce((a, l) => a + l[k], 0))}</td>`).join("")}</tr></tfoot>` : ""}</table></div>`,
           )
           .join("") || '<p class="muted">No payroll for this site.</p>',
       )
     : ""
 }
 ${can("gl.view") || financial ? panel("Site invoices", "Invoices belong to this location; numbering stays in the company invoice series.", p && canInvoice() ? `<button data-act="inv-prep" data-id="${esc(p.code)}">New site invoice</button>` : "", `<div class="tbl"><table><thead><tr><th>Invoice</th><th>Date</th><th>Seller · TIN · business address</th><th>Buyer · TIN · business address</th><th>Status</th><th class="n">Total</th><th></th></tr></thead><tbody>${invoices.map((i) => `<tr><td>${esc(i.no)}</td><td>${fmtD(i.date)}</td><td>${invoicePartySummary(invoiceParties(i).seller)}</td><td>${invoicePartySummary(invoiceParties(i).buyer)}</td><td>${esc(i.status)}</td><td class="n">${peso(i.gross)}</td><td><button data-act="inv-view" data-id="${esc(i.no)}">View</button></td></tr>`).join("") || '<tr><td colspan="7">No invoices for this site.</td></tr>'}</tbody></table></div>`) : ""}
 ${
   financial
     ? panel(
         "Site expenses",
         "Assign a site when preparing a journal voucher in Accounting.",
         "",
         `<div class="tbl"><table><thead><tr><th>Voucher</th><th>Date</th><th>Description</th><th class="n">Expense</th></tr></thead><tbody>${
           entries
             .filter((j) => j.lines.some((l) => ACC[l.a]?.type === "X"))
             .map(
               (j) =>
                 `<tr><td>${esc(j.id)}</td><td>${fmtD(j.date)}</td><td>${esc(j.memo)}</td><td class="n">${peso(j.lines.filter((l) => ACC[l.a]?.type === "X").reduce((a, l) => a + l.d - l.c, 0))}</td></tr>`,
             )
             .join("") ||
           '<tr><td colspan="4">No posted expenses for this site.</td></tr>'
         }</tbody></table></div>`,
       )
     : ""
 }`;
};
